import { useEffect } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';

import {
  type BucketUserArgs,
  bucketUserExperiments,
  getPreviewExperiments,
} from './api';

import { RQ_PREVIEW_EXPS_CACHE } from '@/modules/ReactQuery/cacheKeys';
import { getPersistentId } from '@/modules/activityTracker/helpers';
import { type BucketExperimentsResponse } from '@/modules/experiments/aliases';
import { type OpzlyExperimentName } from '@/modules/experiments/config';
import { checkVariantMismatches } from '@/modules/experiments/helpers/checkVariantMismatches';
import { filterUnbucketedExperiments } from '@/modules/experiments/helpers/filterUnbucketedExperiments';
import { getExperimentsDecision } from '@/modules/experiments/helpers/getExperimentsDecision';
import { logBucketingError } from '@/modules/experiments/helpers/logBucketingError';
import { updateExperimentsBucketedFlag } from '@/modules/experiments/helpers/updateExperimentsBucketedFlag';
import {
  type AdditionalAttributes,
  type CachedExperiments,
  type OpzlyExperimentValue,
} from '@/modules/experiments/types';
import {
  type AxiosCompatibleError,
  type AxiosCompatibleResponse,
} from '@/modules/http/types';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation';
import { getLogger } from '@/modules/observability/logging';

const BUCKET_RETRIES = 5;
const MUTATION_KEY = 'BUCKET_USER_MUTATION';

type HookResult<T extends OpzlyExperimentName> = {
  [K in T]: OpzlyExperimentValue<K>;
};
type HookResultWithBucketer<T extends OpzlyExperimentName> = [
  HookResult<T>,
  (overrideAttributes?: AdditionalAttributes) => void,
];

type UseExperimentsOptions<D> = {
  deferred?: D;
  /**
   * Extra user attributes forwarded to Optimizely on the bucketing call, for
   * attributes known at render time. Values passed to the deferred `bucket()`
   * function take precedence over these.
   *
   * These only have an effect on the *first* bucketing call for the given
   * experiments. The `bucketed` flag lives in a React Query cache with an
   * infinite `staleTime`/`gcTime`, so once an experiment has been bucketed -
   * anywhere: this hook's mount effect, another component using the same
   * experiment, or `SERVERSIDE_bucketUserExperiments` in a parent layout -
   * every later call is a no-op and any attributes passed to it are silently
   * dropped, not just delayed.
   */
  additionalAttributes?: AdditionalAttributes;
};

/**
 * Hook to use JIT Configured Optimizely experiments.
 * @param experimentNames Experiment names passed in **must** be configured in configs/optimizely.ts
 */
export function useExperiments<T extends OpzlyExperimentName>(
  experimentNames: T[]
): HookResult<T>;
export function useExperiments<
  D extends boolean,
  T extends OpzlyExperimentName,
>(
  experimentNames: T[],
  options?: UseExperimentsOptions<D>
): D extends true ? HookResultWithBucketer<T> : HookResult<T>;

export function useExperiments<D extends boolean>(
  experimentNames: OpzlyExperimentName[],
  options?: UseExperimentsOptions<D>
) {
  const client = useQueryClient();
  const { location } = useCurrentLocation();
  const searchParams = useSearchParams();

  const bucketUserMutation = useMutation<
    AxiosCompatibleResponse<BucketExperimentsResponse>,
    AxiosCompatibleError,
    Omit<BucketUserArgs, 'persistentId'>
  >({
    mutationFn: async (args: Omit<BucketUserArgs, 'persistentId'>) => {
      const persistentId = (await getPersistentId()) as string;
      return bucketUserExperiments({
        ...args,
        persistentId,
      });
    },

    retry: BUCKET_RETRIES,

    // e.g. 3 * 100 == 300ms delay before retry #4
    retryDelay: (count) => count * 100,
  });

  const previousPreviewExperimentsCache =
    client.getQueryData<CachedExperiments>([RQ_PREVIEW_EXPS_CACHE]);

  /**
   * Preview experiments are fetched server-side on page load. This useQuery hook
   * serves two important purposes:
   *  1. it sets the caching and TTL to Infinity for the preview experiments data so
   *     that it is never garbage collected
   *  2. it contains the code to perform a refetch client-side if the cache is
   *     invalidated manually (e.g. after signup)
   */
  const { data: cachedExps } = useQuery({
    queryKey: [RQ_PREVIEW_EXPS_CACHE],

    queryFn: async () => {
      const res = await getPreviewExperiments({
        persistentId: (await getPersistentId()) as string,
        countryCode: searchParams.get('country_code') || location,
      });

      /**
       * As in the original server-side fetch, we augment the result with the `bucketed`
       * boolean to track whether the experiment has been seen yet.
       * On refetch we need to retain this information, so we access the previous contents
       * of the query cache.
       */
      return Object.keys(res.data).reduce<CachedExperiments>((acc, val) => {
        acc[val] = {
          decision: res.data[val].variant,
          bucketed: previousPreviewExperimentsCache?.[val].bucketed ?? false,
          variables: res.data[val].flag_variables,
        };
        return acc;
      }, {});
    },
    gcTime: Infinity,
  });

  /**
   * Deferred bucketer returned when `options.deferred` is true.
   *
   * `overrideAttributes` are only sent if this is the *first* time the given
   * experiments are being bucketed. Once an experiment is marked `bucketed`
   * in the preview cache - by a previous call to this function, the mount
   * effect, another component, or a parent layout's
   * `SERVERSIDE_bucketUserExperiments` - it is excluded from
   * `expsToBucket` below and any attributes passed here are dropped, not
   * queued for later.
   */
  function bucket(overrideAttributes?: AdditionalAttributes) {
    if (typeof window === 'undefined') {
      getLogger().error(
        'Please ensure bucketing happens in a client-side operation for these experiments',
        { experimentNames }
      );

      throw new Error(
        `Bucketing attempt detected on the server. Please check the server logs.`
      );
    }

    /**
     * Due to race conditions when rendering this hook in a mapped list (e.g. product list)
     * it's possible that we could send multiple bucketing calls by mistake if this hook
     * is invoked in quick succession.
     *
     * This code checks if there are running mutations with the same experiment names as
     * passed in, to prevent duplicate calls.
     */
    const runningBucketCalls = client.isMutating({
      mutationKey: [MUTATION_KEY],
      predicate: (mutation) => {
        return (
          mutation.state.variables as { experimentNames: string[] }
        )?.experimentNames.every(
          (name: string, index) => name === experimentNames[index]
        );
      },
    });

    if (runningBucketCalls > 0) {
      return;
    }

    const expsToBucket = filterUnbucketedExperiments(
      experimentNames,
      cachedExps
    );

    if (expsToBucket.length) {
      /**
       * Optimistically update the cache before the network call
       * with bucketed: true for each experiment passed into the
       * hook to avoid race conditions. If the call fails we then
       * set them back to false below.
       */
      updateExperimentsBucketedFlag(client, experimentNames, true);

      bucketUserMutation.mutate(
        {
          experimentNames: expsToBucket,
          countryCode: searchParams.get('country_code') || location,
          date: Date.now(),
          referer: document.referrer || null,
          additionalAttributes: {
            ...options?.additionalAttributes,
            ...overrideAttributes,
          },
        },
        {
          onSuccess: (
            res: AxiosCompatibleResponse<BucketExperimentsResponse>
          ) => {
            checkVariantMismatches(cachedExps, res.data);
          },
          onError: (error: AxiosCompatibleError | Error) => {
            /**
             * If the bucketing call failed, make sure to set bucketed back
             * to false here so we can attempt again in the future
             */
            updateExperimentsBucketedFlag(client, experimentNames, false);
            logBucketingError(expsToBucket, error, BUCKET_RETRIES);
          },
        }
      );
    } else if (overrideAttributes) {
      /**
       * All of the requested experiments are already bucketed, so this call
       * is a no-op - see the JSDoc above. Only log here, at debug level,
       * since this hook renders inside mapped lists and this combination
       * (attributes passed to an already-bucketed experiment) is otherwise
       * silent and easy to miss when debugging why an attribute never
       * reached Optimizely.
       */
      getLogger().debug(
        '[JIT Bucketing] Discarded additionalAttributes: all requested experiments are already bucketed',
        { experimentNames, overrideAttributes }
      );
    }
  }

  useEffect(() => {
    /**
     * If cachedExps is null or undefined, assume the preview endpoint
     * call failed and don't try and bucket users. Instead, we return
     * the fallbackExps object defined above which should give users
     * the control experience.
     */
    if (!cachedExps) {
      return;
    }

    /**
     * If the user has specified deferred bucketing, it means
     * they want to control when bucketing occurs rather than
     * letting the hook bucket on mount
     */
    if (options?.deferred) {
      return;
    }

    bucket();
  }, []);

  const returnValue = getExperimentsDecision(experimentNames, cachedExps);

  return options?.deferred ? [returnValue, bucket] : returnValue;
}
